import { CurrencyPipe, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

import { AuthService } from '@app/core/auth';
import { environment } from '@app/env-service';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

@Injectable({
  providedIn: 'root',
})
export class DefectiveReportService {
  url :string = environment.url;
  clientConfig :any = environment.clientConfig;

  curPipe :CurrencyPipe;
  datePipe :DatePipe;

  excelFileType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  excelFileExtension = '.xlsx';

  private saveExcelFile(buffer :any, fileName :string, ext ?:string) :void {
    const data :Blob = new Blob([buffer], { type: this.excelFileType });
    FileSaver.saveAs(data, fileName + (ext ? ext : this.excelFileExtension));
  }

  defectTypes :any[] = [
    { id: 10, label: 'Manufacture' },
    { id: 20, label: 'Production' },
    { id: 90, label: 'Other' },
  ];

  private readonly _defectiveReports = new BehaviorSubject<any>(<any>[]);
  public defectiveReports$ = this._defectiveReports.asObservable();

  public gettingRecords :boolean = false;

  constructor(
    private translate :TranslateService,
    private http :HttpClient,
  ) {
    this.curPipe = new CurrencyPipe('en');
    this.datePipe = new DatePipe('en');
  }

  // po list
  get(params) {
    this.gettingRecords = true;

    return this.http
      .get(`${this.url}/mt_defective_reports`, { params: params })
      .subscribe((resp :any) => {
        this.gettingRecords = false;
        this._defectiveReports.next(resp);
      });
  }

  getAsync(params) {
    return this.http.get(`${this.url}/mt_defective_reports`, {
      params: params,
    });
  }

  getTotalAsync(params :any) {
    params.total = 1;

    return this.http.get(`${this.url}/mt_defective_reports`, {
      params: params,
    });
  }

  // form po
  updateDefectiveReport(
    defectiveReportId :number,
    orderData :any,
  ) :Observable<any> {
    return this.http
      .post(`${this.url}/mt_defective_reports/${defectiveReportId}`, orderData)
      .pipe(
        tap((resp :any) => {
          this.tryUpdateOrAddResp(resp);
        }),
      );
  }

  createDefectiveReport(orderData :any) :Observable<any> {
    return this.http.post(`${this.url}/mt_defective_reports`, orderData).pipe(
      tap((resp :any) => {
        setTimeout(() => {
          const defectiveReports :any = this._defectiveReports.getValue();
          if (defectiveReports && defectiveReports.data) {
            defectiveReports.data.splice(0, 0, resp);
            this._defectiveReports.next(defectiveReports);
          }
        });
        this.reset();
      }),
    );
  }

  deleteDefectiveReport(defectiveReportId :number) {
    return this.http
      .delete(`${this.url}/mt_defective_reports/${defectiveReportId}`)
      .pipe(
        tap((resp :any) => {
          const defectiveReports :any = this._defectiveReports.getValue();
          const poIdx = defectiveReports.data.findIndex(
            (row) => row.id == defectiveReportId,
          );
          defectiveReports.data.splice(poIdx, 1);
          this._defectiveReports.next(defectiveReports);
          this.reset();
        }),
      );
  }

  tryUpdateOrAddResp(resp) {
    const defectiveReports :any = this._defectiveReports.getValue();
    const poIdx = defectiveReports.data.findIndex((row) => row.id == resp.id);
    if (poIdx >= 0) {
      defectiveReports.data[poIdx] = resp;
    } else {
      defectiveReports.data.splice(0, 0, resp);
    }
    this._defectiveReports.next(defectiveReports);
    this.reset();
  }

  deleteDefectiveReportFile(defectiveReportId :number, fileId :number) {
    return this.http
      .delete(
        `${this.url}/mt_defective_reports/${defectiveReportId}/files/${fileId}`,
      )
      .pipe(
        tap((resp :any) => {
          this.tryUpdateOrAddResp(resp);
        }),
      );
  }

  reset() {}

  exportRFQtoPDF(defectiveReport :any) {
    this.getImgDataUrlValue(
      environment.clientConfig.assetImgDir + 'logo-print.png',
      (imgUri :any) => {
        this.createPDF(defectiveReport, imgUri);
      },
    );
  }

  createPDF(defectiveReport, logoUri) {
    // prepare table data
    const tableData :any[] = [
      [
        { text: 'Received Date', bold: true },
        { text: 'SKU', bold: true },
        { text: 'Product Name', bold: true },
        { text: 'PO #', bold: true },
        { text: 'Factory', bold: true },
        { text: 'Quantity', bold: true },
        { text: 'UOM', bold: true },
        { text: 'Unit Price', bold: true },
        { text: 'Line Total', bold: true },
        { text: 'Remarks', bold: true },
      ],
    ];

    defectiveReport.defective_report_rfq.forEach((rfq) => {
      const uom = rfq?.rfq?.unit_of_measures?.find(
        (uom) => uom.id === rfq.rfq?.default_selling_unit_id,
      );
      const data = [
        {
          text: this.datePipe.transform(Number(rfq.received_date), 'mediumDate'),
        },
        { text: rfq.sku },
        { text: rfq.product_name },
        { text: rfq.purchase_order_id.toString() },
        { text: rfq.factory_name },
        { text: rfq.quantity, alignment: 'right' },
        { text: uom?.name },
        {
          text: this.curPipe.transform(Number(rfq.unit_price), ''),
          alignment: 'right',
        },
        {
          text: this.curPipe.transform(Number(rfq.line_total), ''),
          alignment: 'right',
        },
        { text: rfq.remarks },
      ];
      tableData.push(data);
    });

    // prepare header
    const headerContent = [
      {
        columns: [
          {
            alignment: 'left',
            image: logoUri,
            width: 250,
            style: 'logo',
          },
          [
            {
              text: 'Defective Report',
              alignment: 'right',
              style: 'header',
            },
            {
              text:
                'Reported Date:  ' +
                this.datePipe.transform(
                  defectiveReport.reported_date,
                  'mediumDate',
                ),
              alignment: 'right',
            },
            {
              text: 'Reported By:  ' + defectiveReport.reported_by,
              alignment: 'right',
            },
          ],
        ],
      },
    ];

    const footerContent = [
      {
        columns: [
          { text: 'Total Units:', alignment: 'right' },
          {
            text: this.curPipe.transform(
              Number(defectiveReport.total_quantity),
              ' ',
              'symbol',
              '1.0',
            ),
            alignment: 'right',
            width: 120,
          },
        ],
      },
      {
        columns: [
          { text: 'Total Cost:', alignment: 'right' },
          {
            text: this.curPipe.transform(Number(defectiveReport.total_cost)),
            alignment: 'right',
            width: 120,
          },
        ],
      },
    ];

    const tableContent :any[] = [
      {
        text: 'Purchase Order RFQs / Products',
        alignment: 'left',
        style: 'header',
      },
      {
        style: 'table',
        table: {
          widths: [
            'auto', // Received Date
            '*', // SKU
            'auto', // Product Name
            'auto', // PO #
            'auto', // Factory
            'auto', // Quantity
            'auto', // UOM
            'auto', // Unit Price
            'auto', // Line Total
            'auto', // Remarks
          ],
          headerRows: 1,
          body: tableData,
        },
        // layout: 'headerLineOnly'
      },
    ];

    let pdfContent :any[] = [];

    pdfContent = pdfContent.concat(headerContent, tableContent, footerContent);

    const documentDefinition = {
      pageSize: 'letter',
      pageOrientation: 'landscape',

      content: pdfContent,

      defaultStyle: {
        fontSize: 10,
        columnGap: 20,
      },
      styles: {
        logo: {
          fillColor: '#ee0000',
        },
        product: {
          fontSize: 15,
          margin: [0, 20, 0, 5],
          bold: true,
        },
        category: {
          fontSize: 14,
        },
        shipping: {
          margin: [0, 25, 0, 0],
        },
        header: {
          fontSize: 16,
          bold: false,
          margin: [0, 30, 0, 5],
          padding: [0, 0, 0, 0],
        },
        subheader: {
          fontSize: 14,
          bold: false,
          margin: [0, 0, 0, 0],
          padding: [0, 0, 0, 0],
        },
        table: {
          margin: [0, 5, 0, 15],
        },
        tableHeader: {
          bold: true,
          fontSize: 13,
          color: 'black',
        },
      },
    };
    Swal.close();
    pdfMake.createPdf(documentDefinition).download('DefectiveReport.pdf');
  }

  exportListtoCSV(defectiveReports :any) {
    const fileName :string = 'DefectiveReport';
    // Records sheet
    const jsonData :any = [];

    let prevTransactionId = 0;
    defectiveReports.forEach((dr) => {
      if (dr.defective_report_rfq) {
        dr.defective_report_rfq.forEach((rfq :any) => {
          // Extract UOM name from unit_of_measures
          const uomName =
            rfq.rfq && rfq.rfq.unit_of_measures
              ? rfq.rfq.unit_of_measures.find(
                  (uom) => uom.id === rfq.purchase_order_rfq?.ordered_uom_id,
                )?.name || 'N/A'
              : 'N/A';

          jsonData.push({
            'Transaction #': dr.id.toString(),
            'Reported Date': this.datePipe.transform(
              dr.reported_date,
              'MM/dd/yyyy',
            ),
            'Reported By': dr.reported_by,
            Factory: rfq.factory_name,
            SKU: rfq.sku,
            'Product Name': rfq.product_name,
            'Product Dept':
              rfq.purchase_order_rfq && rfq.purchase_order_rfq.rfq_dept
                ? rfq.purchase_order_rfq.rfq_dept.product_dept
                : '',
            'Unit Price': rfq.unit_price,
            'Received Qty': rfq.quantity,
            UOM: uomName, // Add UOM column here
            'Line Total': this.curPipe.transform(Number(rfq.line_total), ''),
            'Total Units': prevTransactionId !== dr.id ? dr.total_quantity : '',
            'Total Cost':
              prevTransactionId !== dr.id
                ? this.curPipe.transform(Number(dr.total_cost), '')
                : '',
          });

          if (prevTransactionId !== dr.id) prevTransactionId = dr.id;
        });
      }
    });

    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);

    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'csv',
      type: 'array',
    });

    Swal.close();

    this.saveExcelFile(excelBuffer, fileName, '.csv');
  }

  exportListtoPDF(defectiveReports :any) {
    this.getImgDataUrlValue(
      environment.clientConfig.assetImgDir + 'logo-print.png',
      (imgUri :any) => {
        this.createListPDF(defectiveReports, imgUri);
      },
    );
  }

  createListPDF(defectiveReports, logoUri) {
    // prepare table data
    let totalUnits :number = 0;
    let totalCost :number = 0;

    const tableData :any[] = [
      [
        { text: 'Transaction #', bold: true },
        { text: 'Reported Date', bold: true },
        { text: 'Reported By', bold: true },
        { text: 'Factory', bold: true },
        { text: 'SKU', bold: true },
        { text: 'Product Name', bold: true },
        { text: 'Product Dept', bold: true },
        { text: 'Unit Price', bold: true },
        { text: 'Received Qty', bold: true },
        { text: 'UOM', bold: true }, // New UOM Column
        { text: 'Line Total', bold: true },
        { text: 'Total Units', bold: true },
        { text: 'Total Cost', bold: true },
      ],
    ];

    let prevTransactionId = 0;
    defectiveReports.forEach((dr :any) => {
      totalUnits += dr.total_quantity;
      totalCost += dr.total_cost;

      if (dr.defective_report_rfq) {
        dr.defective_report_rfq.forEach((rfq :any) => {
          // Extract UOM name from unit_of_measures
          const uomName =
            rfq.rfq && rfq.rfq.unit_of_measures
              ? rfq.rfq.unit_of_measures.find(
                  (uom) => uom.id === rfq.purchase_order_rfq?.ordered_uom_id,
                )?.name || 'N/A'
              : 'N/A';

          const data :any = [
            { text: dr.id.toString() },
            { text: this.datePipe.transform(dr.reported_date, 'MM/dd/yyyy') },
            { text: dr.reported_by },
            { text: rfq.factory_name },

            { text: rfq.sku },
            { text: rfq.product_name },
            {
              text:
                rfq?.purchase_order_rfq && rfq?.purchase_order_rfq?.rfq_dept
                  ? rfq?.purchase_order_rfq?.rfq_dept.product_dept
                  : '',
            },
            {
              text: this.curPipe.transform(Number(rfq.unit_price), ''),
              alignment: 'right',
            },
            { text: rfq.quantity, alignment: 'right' },
            { text: uomName },
            {
              text: this.curPipe.transform(Number(rfq.line_total), ''),
              alignment: 'right',
            },
            {
              text: prevTransactionId !== dr.id ? dr.total_quantity : '',
              alignment: 'right',
            },
            {
              text:
                prevTransactionId !== dr.id
                  ? this.curPipe.transform(Number(dr.total_cost), '')
                  : '',
              alignment: 'right',
            },
          ];

          if (prevTransactionId !== dr.id) prevTransactionId = dr.id;

          tableData.push(data);
        });
      }
    });

    // prepare header
    const headerContent = [
      {
        columns: [
          {
            alignment: 'left',
            image: logoUri,
            width: 250,
            style: 'logo',
          },
          [
            {
              text: 'Defective Report',
              alignment: 'right',
              style: 'header',
            },
          ],
        ],
      },
    ];

    const footerContent = [
      {
        columns: [
          { text: 'Total Units:', alignment: 'right' },
          {
            text: this.curPipe.transform(Number(totalUnits), ' ', 'symbol', '4.0'),
            alignment: 'right',
            width: 120,
          },
        ],
      },
      {
        columns: [
          { text: 'Total Cost:', alignment: 'right' },
          {
            text: this.curPipe.transform(Number(totalCost)),
            alignment: 'right',
            width: 120,
          },
        ],
      },
    ];

    const tableContent :any[] = [
      {
        text: 'Defective Report',
        alignment: 'left',
        style: 'header',
      },
      {
        style: 'table',
        table: {
          widths: [
            'auto',
            'auto',
            'auto',
            'auto',
            'auto',
            '*',
            'auto',
            'auto',
            'auto',
            'auto',
            'auto',
            'auto',
            'auto',
          ],
          headerRows: 1,
          body: tableData,
        },
        // layout: 'headerLineOnly'
      },
    ];

    let pdfContent :any[] = [];

    pdfContent = pdfContent.concat(headerContent, tableContent, footerContent);

    const documentDefinition = {
      pageSize: 'letter',
      pageOrientation: 'landscape',

      content: pdfContent,

      defaultStyle: {
        fontSize: 10,
        columnGap: 20,
      },
      styles: {
        logo: {
          fillColor: '#ee0000',
        },
        product: {
          fontSize: 15,
          margin: [0, 20, 0, 5],
          bold: true,
        },
        category: {
          fontSize: 14,
        },
        shipping: {
          margin: [0, 25, 0, 0],
        },
        header: {
          fontSize: 16,
          bold: false,
          margin: [0, 30, 0, 5],
          padding: [0, 0, 0, 0],
        },
        subheader: {
          fontSize: 14,
          bold: false,
          margin: [0, 0, 0, 0],
          padding: [0, 0, 0, 0],
        },
        table: {
          margin: [0, 5, 0, 15],
        },
        tableHeader: {
          bold: true,
          fontSize: 13,
          color: 'black',
        },
      },
    };
    Swal.close();
    pdfMake.createPdf(documentDefinition).download('DefectiveReport.pdf');
  }

  getImgDataUrlValue(imgUrl :string, callback) {
    const xhr = new XMLHttpRequest();
    xhr.onload = function () {
      const reader = new FileReader();
      reader.onloadend = function () {
        callback(reader.result);
      };
      reader.readAsDataURL(xhr.response);
    };
    xhr.open('GET', imgUrl);
    xhr.responseType = 'blob';
    xhr.send();
  }
}
